/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Gapcursor from '@tiptap/extension-gapcursor';
import Text from '@tiptap/extension-text';
import Document from '@tiptap/extension-document';
import { Superscript } from '@tiptap/extension-superscript';
import { Subscript } from '@tiptap/extension-subscript';
import { TextStyle } from '@tiptap/extension-text-style';
import { NodeSelection } from '@tiptap/pm/state';
import { createMigration, migrateContent, updateMigration } from './migrate';
import { LoreeInteractiveEditorFormatOptions } from './loreeInteractionEditorFormatOptions';
import { CustomizedAnchorElement } from './editorExtensions/anchorElement';
import {
  CustomizedClassAttribute,
  CustomizedStyleAttribute,
  CustomizedUnderLine,
  CustomizedTextAlign,
  CustomizedPlaceholder,
} from './editorExtensions/libraryExtensions';
import { BulletList } from './editorExtensions/bulletListExtension';
import { CustomizedCaption } from './editorExtensions/captionElementExtension';
import { CommandExtension } from './editorExtensions/commands';
import { HardBreak } from './editorExtensions/customHardBreak';
import CustomizedImage from './editorExtensions/customImage';
import { CustomizedTextTransform } from './editorExtensions/textTransformExtension';
import { CustomizedHeading } from './editorExtensions/heading';
import { CustomizedIframeElement } from './editorExtensions/iframeElement';
import { CustomizedParagraph, EmptySpan } from './editorExtensions/paragraph';
import { CustomizedFontFamily } from './editorExtensions/fontFamily';
import { CustomizedFontSize } from './editorExtensions/fontSize';
import { LineHeight } from './editorExtensions/lineHeight';
import { ListItem } from './editorExtensions/listItemExtension';
import { OrderedList } from './editorExtensions/orderedListExtension';
import { Padding } from './editorExtensions/padding';
import { Table } from './editorExtensions/tableExtension';
import { CustomizedTableColGroup } from './editorExtensions/tableColGroup';
import { CustomizedTableHead } from './editorExtensions/tableHead';
import { CustomizedTableRow } from './editorExtensions/tablerow';
import { CustomizedTableCell } from './editorExtensions/tableCell';
import { CustomizedTableBody } from './editorExtensions/tableBody';
import { CustomizedTableColumn } from './editorExtensions/tableColumn';
import { CustomizedTableHeader } from './editorExtensions/tableHeader';
import { TextIndent } from './editorExtensions/textIndent';
import { Color } from './editorExtensions/color';
import { BackgroundColor } from './editorExtensions/backgroundColor';
import { CustomizedBlockQuote } from './editorExtensions/blockquoteExtension';
import { BorderColor } from './editorExtensions/borderColor';
import { CustomizedVideoElement } from './editorExtensions/videoExtension';
import { IconTag } from './editorExtensions/iconTagElement';
import { CONSTANTS } from './editorUtilityFunctions/constants';
import { LoreeInteractiveEditorType } from './editorUtilityFunctions/lintEditorType';
import './editorUtilityFunctions/loreeInteractiveEditorCommonStyles.scss';
import {
  EditorTopSection,
  LoreeInteractiveEditorWrapper,
} from './editorUtilityFunctions/styledContents';
import { hideLinkInfoPopup, showLinkDetails } from '../linkPopup/showLinkInfoPopup';
import { CustomAlignment } from './editorExtensions/customAlignment';
import { ReactComponent as GreaterThanIcon } from './editorFormattingIcons/greaterThanIcon.svg';
import { items } from './editorUtilityFunctions/embedUtil';
import SafeSelectAll from './editorExtensions/selectionExtension';
import { CustomizedFontStyle } from './editorExtensions/fontStyle';
import { connect, ConnectedProps } from 'react-redux';
const mapState = ({ form }: any) => ({
  fontColor: form.fontColor,
  fontFamily: form.fontFamily,
  fontStyle: form.fontStyle,
  fontSize: form.fontSize,
});
const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & LoreeInteractiveEditorType;

const LoreeInteractiveEditor = (props: Props) => {
  const {
    initialValue,
    editorConfig,
    updateEditorContent,
    indexValue,
    type,
    titleValue,
    selection,
    subSelection,
  } = props;
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isCalledMigration, setIsCalledMigration] = useState(true);
  const [fontStyleValue, setFontStyleValue] = useState('');
  const editorContent =
    migrateContent(initialValue, type, props) ?? CONSTANTS.LOREE_EDITOR_EMPTY_CONTENT;
  const migrationDone = useRef(false);
  const wasEmpty = useRef(true);
  const popperID = `${CONSTANTS.EDITOR_POPPER_ID}-${indexValue}`;
  const editorRef: React.MutableRefObject<Editor | null> = useRef(null);
  const lintEditor: Editor | null = useEditor({
    extensions: [
      StarterKit,
      Text,
      HardBreak,
      EmptySpan,
      CommandExtension,
      Document.extend({ marks: 'link' }),
      ListItem,
      BulletList,
      OrderedList,
      Superscript,
      Subscript,
      TextStyle,
      CustomizedPlaceholder(editorConfig),
      CustomizedAnchorElement,
      CustomizedIframeElement,
      CustomizedClassAttribute,
      CustomizedStyleAttribute,
      CustomizedTextTransform,
      CustomizedTextAlign,
      CustomizedHeading,
      CustomizedParagraph,
      CustomizedImage,
      CustomizedUnderLine,
      CustomizedFontFamily,
      CustomizedFontSize,
      CustomizedFontStyle,
      CustomizedVideoElement,
      Color,
      BackgroundColor,
      BorderColor,
      LineHeight,
      CustomizedBlockQuote,
      Table,
      CustomizedCaption,
      CustomizedTableColGroup,
      CustomizedTableHead,
      CustomizedTableRow,
      CustomizedTableCell,
      CustomizedTableBody,
      CustomizedTableColumn,
      CustomizedTableHeader,
      IconTag,
      TextIndent,
      Padding,
      Gapcursor,
      CustomAlignment,
      SafeSelectAll,
    ],
    autofocus: type === 'DragandDrop' || type === 'Hotspot' ? false : true,
    content: editorContent,
    onUpdate({ editor }) {
      updateMigration(editor, editorConfig, setFontStyleValue);
      const isEmpty = editor.isEmpty;
      if (!migrationDone.current && isEmpty) {
        migrationDone.current = true;
        wasEmpty.current = true;
        updateEditorContent(editor.getHTML());
        return;
      }
      if (!wasEmpty.current && isEmpty) {
        wasEmpty.current = true;
        updateEditorContent(editor.getHTML());
        return;
      }
      wasEmpty.current = isEmpty;
      updateEditorContent(editor.getHTML());
    },

    onCreate({ editor }) {
      createMigration(editor, editorConfig);
    },
    editorProps: {
      handleDOMEvents: {
        drop: (_view, e) => {
          e.preventDefault();
        },
        dragstart: (_view, e) => {
          e.preventDefault();
        },
        click: (_view, e) => {
          const currentEditor = editorRef.current;
          if (!currentEditor) return;
          const target = e.target as HTMLElement;
          const isAudioTarget = Boolean(target.closest?.('audio'));
          const selection = currentEditor.state.selection as NodeSelection;
          showLinkDetails(
            target,
            currentEditor,
            isAudioTarget ? 'iframe' : selection?.node?.type.name ?? '',
            popperID,
          );
          const isMediaTarget = target.tagName === 'IMG' || isAudioTarget;
          if (!isMediaTarget && !selection?.empty && !selection?.node?.type.name) {
            hideLinkInfoPopup();
          }
        },
        focusin: (_view, e) => {
          const target = e.target as HTMLElement;
          if (!target.closest?.('audio')) return;
          const currentEditor = editorRef.current;
          if (!currentEditor) return;
          showLinkDetails(target, currentEditor, 'iframe', popperID);
        },
      },
    },
  });
  editorRef.current = lintEditor;
  useEffect(() => {
    if (lintEditor && isCalledMigration) {
      const currentContent = lintEditor.getHTML();
      updateEditorContent(currentContent);
      if (currentContent !== editorContent) {
        lintEditor.commands.setContent(currentContent);
        updateMigration(lintEditor, editorConfig, setFontStyleValue);
        setIsCalledMigration(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorContent, isCalledMigration, lintEditor]);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFullScreen(false);
      }
    };

    if (isFullScreen) {
      window.addEventListener('keydown', handleKeyDown);
    } else {
      window.removeEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullScreen]);
  return (
    <EditorTopSection className={`editor-wrapper ${isFullScreen ? 'fullscreen' : ''}`}>
      {isFullScreen && (
        <div className='fullScreenHeader' style={{ borderRadius: '0px' }}>
          <div className='typeOfFullScreen'>
            {' '}
            {items.find((item) => item.key === type)?.label || type}
          </div>
          <GreaterThanIcon />
          <div className='dynamicText' title={titleValue?.trim() === '' ? 'Title' : titleValue}>
            {titleValue?.trim() === '' ? 'Title' : titleValue}
          </div>
          {selection !== '' && (
            <>
              <GreaterThanIcon />
              <div className='typeOfFullScreen'>{selection}</div>
            </>
          )}
          {subSelection !== '' && (
            <>
              <GreaterThanIcon />
              <div className='typeOfFullScreen'>{subSelection}</div>
            </>
          )}
        </div>
      )}

      <LoreeInteractiveEditorFormatOptions
        editor={lintEditor}
        editorConfig={editorConfig}
        config={{ isFullScreen: isFullScreen, setIsFullScreen: setIsFullScreen }}
        fontStyleValue={fontStyleValue}
        type={type}
      />
      <EditorContent className={CONSTANTS.LOREE_INTERACTIVE_TIPTAP_EDITOR} editor={lintEditor}>
        <LoreeInteractiveEditorWrapper>
          <div id={popperID} />
        </LoreeInteractiveEditorWrapper>
      </EditorContent>
    </EditorTopSection>
  );
};

export default connector(LoreeInteractiveEditor);
